// Dataset + field-component switching.
//
// Owns the two top-level orchestrators that fire when the user picks a new
// dataset or active field component from the Data folder:
//   - switchDataset()         reload HDF5, recreate slices/grid/volume/seeds
//   - switchFieldComponent()  recompute colormap bounds, redraw, rebuild section
//
// Both write to viewer.{field,grid,objects} and rebuild the affected
// pane sections in place by dispatching store intents.

import * as THREE from 'three';
import { params, store } from '../store/instance';
import { physicalToThreeJS, threeJSToPhysical } from '@adapters/coordinates';
import { createFieldlineCore } from '@adapters/fieldlines';
import { initFieldlineWorker } from '../workers/fieldlineClient';
import { loadFieldMetadata, loadFieldComponents } from '@app/fieldData';
import { seedFromConfig as seedFieldlines } from '../scene/fieldlines';
import { initSelectionInteractions } from '@scene/picking';
import { updateSelectionVisuals } from '@scene/selection';
import { calculateColormapBoundsFromArray, updateColorbarLabels } from '@ui/colorbar';
import { syncColormap as refreshColormap } from '@app/colormapSync';
import {
    refreshOpenDetails as refreshCoordOverlayDetails,
    updateCoordinateSystemDisplay as updateCoordOverlay,
    isOverlayMounted as coordOverlayMounted,
} from '../ui/coordinateDisplay';
import { uploadedDatasets } from './uploadedDatasets';
import { INTENTS } from '../store/intents';
import { debugLog } from '@adapters/debug';
import type { ViewerContext } from './context';

function currentFieldArray(viewer: ViewerContext): Float32Array | null | undefined {
    const f = viewer.field;
    return (f.currentComponent === 'magnitude') ? f.gridFlat : (f.data ? f.data[f.currentComponent] : null);
}

/** Load HDF5 metadata (grid shape, axis extents, units) into viewer. */
export async function loadFieldMetadataFromHDF5(viewer: ViewerContext, hdf5Url: string | null): Promise<boolean> {
    if (!hdf5Url) return false;
    return loadFieldMetadata(viewer, hdf5Url);
}

/**
 * Allocate a zero-filled scalar grid so the app can still render slices and
 * UI when the HDF5 dataset is missing or fails to load.
 */
export function seedZeroField(viewer: ViewerContext): void {
    const total = viewer.grid.nx * viewer.grid.ny * viewer.grid.nz;
    viewer.field.gridFlat = new Float32Array(total);
    store.dispatch(INTENTS.SET_COLORMAP_RANGE, { min: 0.0, max: 1.0 });
}

/**
 * Load HDF5 metadata then components. Returns true only if both succeeded.
 */
export async function tryLoadField(viewer: ViewerContext): Promise<boolean> {
    const metadataLoaded = await loadFieldMetadataFromHDF5(viewer, viewer.field.hdf5Url);
    if (!metadataLoaded) {
        console.error('Failed to load field metadata. Cannot proceed.');
        alert('Failed to load field metadata from HDF5.');
        return false;
    }
    return !!(await loadFieldComponentsFromHDF5(viewer, viewer.field.hdf5Url));
}

/**
 * Load HDF5 field components (Bx/By/Bz + magnitude). On success, recompute
 * colormap bounds for the active component.
 */
export async function loadFieldComponentsFromHDF5(viewer: ViewerContext, url: string | null): Promise<boolean> {
    if (!url) return false;
    const ok = await loadFieldComponents(viewer, url, params);
    if (!ok) return false;
    applyComponentColormapDefaults(viewer);
    store.dispatch(INTENTS.UI_PANE_REFRESH);
    updateColorbarLabels({ colormapMin: params.colormapMin, colormapMax: params.colormapMax, fieldMetadata: viewer.field.metadata, unitsMode: params.colorbarUnits });
    refreshColormap(viewer, params);
    return true;
}

import type { ColormapStatsResult } from '@ui/colorbar';
import {
    createBinding,
    findBindingByField,
    repointPrimitive,
    updateBindingFields,
} from '../store/bindings';
import type { PrimitiveKey } from '../store/schema';

function applyComponentColormapDefaults(viewer: ViewerContext): ColormapStatsResult {
    const data = currentFieldArray(viewer);
    const stats = calculateColormapBoundsFromArray(data, { centerOnZero: params.centerOnZero });
    store.dispatch(INTENTS.SET_COLORMAP_RANGE, { min: stats.minBound, max: stats.maxBound });
    store.dispatch(INTENTS.SET_CENTER_ON_ZERO, { value: stats.hasBothSigns });

    // Stage 2: ensure a binding exists for the active field component and
    // every primitive points at it. Bootstrap path on dataset load keeps
    // legacy global colormap fields and the binding in sync — once the
    // render side reads exclusively from the binding (Tasks 3+4), the
    // legacy fields go away.
    const field = viewer.field.currentComponent || params.fieldComponent || 'magnitude';
    const units = (viewer.field.metadata && viewer.field.metadata.field_units)
        ? viewer.field.metadata.field_units
        : (viewer.sceneMeta.units || '');
    const seed = {
        colormap: params.colormap,
        min: stats.minBound,
        max: stats.maxBound,
        log: params.colormapLog,
        units,
        centerOnZero: stats.hasBothSigns,
        transparentBounds: params.transparentBounds,
    };
    let id = findBindingByField(field);
    if (id) {
        updateBindingFields(id, seed);
    } else {
        id = createBinding({ field, seed });
    }
    const primitives: PrimitiveKey[] = ['slices', 'volume', 'contour', 'fieldlines'];
    for (const p of primitives) repointPrimitive(p, id);

    return stats;
}

/**
 * If the volume layer is enabled, capture its current uniform values, recreate
 * it, then restore the uniforms so user settings persist across the switch.
 */
function rebuildVolumeWithPriorUniforms(viewer: ViewerContext): void {
    if (!params.volumeEnabled) return;
    const vol = viewer.objects.volume;
    const oldMat = vol.material as THREE.ShaderMaterial | null;
    const oldUniforms = oldMat ? oldMat.uniforms : null;
    const prevSteps = (oldUniforms && oldUniforms['uSteps']) ? oldUniforms['uSteps'].value as number : params.volumeSteps;
    const prevDensity = (oldUniforms && oldUniforms['uDensity']) ? oldUniforms['uDensity'].value as number : params.volumeDensity;
    const prevBrightness = (oldUniforms && oldUniforms['uBrightness']) ? oldUniforms['uBrightness'].value as number : params.volumeBrightness;
    store.dispatch(INTENTS.SCENE_SYNC_VOLUME);
    const newMat = vol.material as THREE.ShaderMaterial | null;
    const u = newMat ? newMat.uniforms : null;
    if (!u) return;
    if (u['uSteps']) u['uSteps'].value = prevSteps;
    if (u['uDensity']) u['uDensity'].value = prevDensity;
    if (u['uBrightness']) u['uBrightness'].value = prevBrightness;
    if (vol.scalarMin !== null && vol.scalarMax !== null) {
        const range = (vol.scalarMax - vol.scalarMin) || 1;
        if (u['uTransparentBounds']) u['uTransparentBounds'].value = params.transparentBounds ? 1.0 : 0.0;
        if (u['uColorMinNorm']) u['uColorMinNorm'].value = Math.max(0.0, Math.min(1.0, (params.colormapMin - vol.scalarMin) / range));
        if (u['uColorMaxNorm']) u['uColorMaxNorm'].value = Math.max(0.0, Math.min(1.0, (params.colormapMax - vol.scalarMin) / range));
    }
}

function updateCoordinateSystemDisplay(viewer: ViewerContext): void {
    if (!coordOverlayMounted()) return;
    updateCoordOverlay({
        coordinateSystem: viewer.sceneMeta.coordinateSystem,
        units: viewer.sceneMeta.units,
        camera: viewer.three.camera,
        threeJSToPhysical,
    });
}

function dispatchSliceParamsFromMeshes(viewer: ViewerContext): void {
    store.dispatch(INTENTS.SET_COLORMAP_RANGE, { min: params.colormapMin, max: params.colormapMax });
    store.dispatch(INTENTS.SET_COLORMAP_UNITS, {
        units: (viewer.field.metadata && viewer.field.metadata.field_units)
            ? viewer.field.metadata.field_units
            : (viewer.sceneMeta.units || ''),
    });
    store.dispatch(INTENTS.SET_SLICE_OPACITY, { value: params.sliceOpacity });
    const s = viewer.objects.slices.state;
    if (s.XY && s.XY.mesh) store.dispatch(INTENTS.SET_SLICE_POSITION, { axis: 'XY', value: Math.round(s.XY.mesh.position.y * 100) / 100 });
    if (s.ZY && s.ZY.mesh) store.dispatch(INTENTS.SET_SLICE_POSITION, { axis: 'ZY', value: Math.round(s.ZY.mesh.position.z * 100) / 100 });
    if (s.ZX && s.ZX.mesh) store.dispatch(INTENTS.SET_SLICE_POSITION, { axis: 'ZX', value: Math.round(s.ZX.mesh.position.x * 100) / 100 });
    store.dispatch(INTENTS.UI_PANE_REFRESH);
}

/**
 * Switch to a different HDF5 dataset.
 */
export async function switchDataset(viewer: ViewerContext, datasetKey: string): Promise<void> {
    debugLog(viewer, 'Switching to dataset:', datasetKey);

    const myToken = Symbol(datasetKey);
    viewer.field.switchToken = myToken;
    const superseded = (): boolean => viewer.field.switchToken !== myToken;

    store.dispatch(INTENTS.CLEAR_CONTOURS);

    const datasetMap = viewer.sceneMeta.datasetMap;
    const datasetEntry = datasetMap[datasetKey];
    let url: string = (datasetEntry && typeof datasetEntry === 'object' && typeof (datasetEntry as { url?: string }).url === 'string')
        ? (datasetEntry as { url: string }).url
        : (typeof datasetEntry === 'string' ? datasetEntry : `data/${datasetKey}.h5`);
    const uploadedUrl = uploadedDatasets.get(datasetKey);
    if (uploadedUrl) {
        url = uploadedUrl;
        datasetMap[datasetKey] = { url };
    }
    viewer.field.hdf5Url = url;
    viewer.field.currentDataset = datasetKey;
    viewer.field.datasetLabel = datasetKey || '';

    viewer.field.gridFlat = null;
    viewer.field.metadata = null;
    viewer.field.data = {};
    store.dispatch(INTENTS.CLEAR_FIELDLINES);

    store.dispatch(INTENTS.UI_LOADING_SHOW, { msg: 'Loading dataset…' });
    const metadataLoaded = await loadFieldMetadataFromHDF5(viewer, viewer.field.hdf5Url);
    if (superseded()) return;
    if (!metadataLoaded) {
        store.dispatch(INTENTS.UI_LOADING_HIDE);
        alert('The uploaded or selected dataset is not suitable: required coordinates or components are missing.');
        return;
    }
    const loadedOK = await loadFieldComponentsFromHDF5(viewer, viewer.field.hdf5Url);
    if (superseded()) return;
    if (!loadedOK) {
        store.dispatch(INTENTS.UI_LOADING_HIDE);
        alert('The uploaded or selected dataset is not suitable: missing vector components (Bx, By, Bz) or incompatible shapes.');
        return;
    }

    updateColorbarLabels({ colormapMin: params.colormapMin, colormapMax: params.colormapMax, fieldMetadata: viewer.field.metadata, unitsMode: params.colorbarUnits });
    updateCoordinateSystemDisplay(viewer);
    refreshCoordOverlayDetails();

    const grid = viewer.grid;
    params.sliceZ  = Math.round(((grid.zMin + grid.zMax) / 2) * 100) / 100;
    params.sliceZY = Math.round(((grid.xMin + grid.xMax) / 2) * 100) / 100;
    params.sliceZX = Math.round(((grid.yMin + grid.yMax) / 2) * 100) / 100;

    store.dispatch(INTENTS.SCENE_REBUILD_SLICES);
    const s = viewer.objects.slices.state;
    if (s.XY && s.XY.mesh) s.XY.mesh.visible = !!params.showFieldSlice;
    if (s.ZY && s.ZY.mesh) s.ZY.mesh.visible = !!params.showFieldSliceZY;
    if (s.ZX && s.ZX.mesh) s.ZX.mesh.visible = !!params.showFieldSliceZX;

    viewer.grid.axisExtents = {
        x: { min: viewer.grid.xMin, max: viewer.grid.xMax },
        y: { min: viewer.grid.yMin, max: viewer.grid.yMax },
        z: { min: viewer.grid.zMin, max: viewer.grid.zMax },
    };
    store.dispatch(INTENTS.SCENE_APPLY_AXIS_GRID);

    store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'slices' });
    syncPositionParamsFromSelection(viewer);
    store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'data' });
    store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'contour' });
    store.dispatch(INTENTS.UI_PANE_REFRESH);

    rebindSelectionInteractions(viewer);
    clampSelectionToGrid(viewer);

    viewer.objects.fieldlines.core = createFieldlineCore({
        THREE,
        grid: viewer.grid,
        getFieldArrays: () => ({
            Bx: viewer.field.data?.['Bx'] ?? null,
            By: viewer.field.data?.['By'] ?? null,
            Bz: viewer.field.data?.['Bz'] ?? null,
        }),
    }) as unknown as ViewerContext['objects']['fieldlines']['core'];

    rebuildVolumeWithPriorUniforms(viewer);
    refreshColormap(viewer, params);

    await initFieldlineWorker(viewer.grid, viewer.field.data?.['Bx'] ?? null, viewer.field.data?.['By'] ?? null, viewer.field.data?.['Bz'] ?? null);
    if (superseded()) return;

    await seedFieldlines(viewer, params, {});
    if (superseded()) return;

    store.dispatch(INTENTS.UI_LOADING_HIDE);
}

/** Switch the active field component (Bx/By/Bz/magnitude) — recompute colormap bounds + redraw. */
export async function switchFieldComponent(viewer: ViewerContext, componentName: string): Promise<void> {
    debugLog(viewer, 'Switching to field component:', componentName);

    const myToken = Symbol('component:' + componentName);
    viewer.field.switchToken = myToken;

    store.dispatch(INTENTS.CLEAR_CONTOURS);
    viewer.field.currentComponent = componentName;

    if (!currentFieldArray(viewer)) return;
    if (viewer.field.switchToken !== myToken) return;
    const stats = applyComponentColormapDefaults(viewer);

    updateColorbarLabels({ colormapMin: params.colormapMin, colormapMax: params.colormapMax, fieldMetadata: viewer.field.metadata, unitsMode: params.colorbarUnits });
    refreshColormap(viewer, params);
    rebuildVolumeWithPriorUniforms(viewer);
    dispatchSliceParamsFromMeshes(viewer);

    debugLog(viewer, `Switched to ${componentName}: colormap range [${stats.minBound.toFixed(4)}, ${stats.maxBound.toFixed(4)}]`);
}

// === Helpers ===

function syncPositionParamsFromSelection(viewer: ViewerContext): void {
    const sel = viewer.objects.selection.invisibleMesh;
    if (!sel) return;
    const phys = threeJSToPhysical(sel.position.x, sel.position.y, sel.position.z);
    const cx = Math.min(viewer.grid.xMax, Math.max(viewer.grid.xMin, phys.x));
    const cy = Math.min(viewer.grid.yMax, Math.max(viewer.grid.yMin, phys.y));
    const cz = Math.min(viewer.grid.zMax, Math.max(viewer.grid.zMin, phys.z));
    store.dispatch(INTENTS.SET_SELECTION_POSITION, { x: cx, y: cy, z: cz });
}

function rebindSelectionInteractions(viewer: ViewerContext): void {
    const dispose = viewer.dispose.selectionInteractions;
    if (typeof dispose === 'function') {
        dispose();
        viewer.dispose.selectionInteractions = null;
    }
    const renderer = viewer.three.renderer;
    const camera = viewer.three.camera;
    const controls = viewer.three.controls;
    const dot = viewer.objects.selection.centerDot;
    const inv = viewer.objects.selection.invisibleMesh;
    if (!renderer || !camera || !dot || !inv) return;
    viewer.dispose.selectionInteractions = initSelectionInteractions({
        THREE,
        renderer,
        camera,
        controls,
        cubeCenterDot: dot,
        invisibleCubeMesh: inv,
        physicalToThreeJS,
        threeJSToPhysical,
        getCoordUnits: () => (viewer.sceneMeta.units || ''),
        getFieldUnits: () => ((viewer.field.metadata && viewer.field.metadata.field_units) ? viewer.field.metadata.field_units : ''),
        getFieldLabel: () => (viewer.field.currentComponent || 'Field'),
    });
}

function clampSelectionToGrid(viewer: ViewerContext): void {
    const sel = viewer.objects.selection.invisibleMesh;
    if (!sel) return;
    const p = sel.position;
    const phys = threeJSToPhysical(p.x, p.y, p.z);
    const cx = Math.min(viewer.grid.xMax, Math.max(viewer.grid.xMin, phys.x));
    const cy = Math.min(viewer.grid.yMax, Math.max(viewer.grid.yMin, phys.y));
    const cz = Math.min(viewer.grid.zMax, Math.max(viewer.grid.zMin, phys.z));
    sel.position.copy(physicalToThreeJS(cx, cy, cz));
    updateSelectionVisuals(viewer, params);
}
